/**
 * Wire types for the enriched-product CSV export lifecycle.
 * Mirrors the workflow-service contract at `/api/v1/exports/**`
 * (reached through the gateway at `/workflow/v1/exports/**`).
 */

/** Export row lifecycle states. Terminal: COMPLETED | FAILED | CANCELLED. */
export type ExportStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

/**
 * Subsidiary/org/code triple sent in the request body. The server cross-checks
 * it against the JWT (mismatch → 404); `subsidiaryCode` builds the export's own
 * human-readable workflow slug.
 */
export interface ExportTenantContext {
  subsidiaryId: string;
  organizationId: string;
  subsidiaryCode: string;
}

/**
 * Optional filter set narrowing which products export. Omitted entirely = "all
 * successful products in the run" (v1 default). `fieldFilters` keys are delivery
 * field wire names; unknown keys → 400.
 */
export interface ExportFilters {
  productIds?: string[];
  successOnly?: boolean;
  categories?: string[];
  minPostScore?: number;
  maxPostScore?: number;
  fieldFilters?: Record<string, string[]>;
  /** Export the run TRANSLATED (`en-GB`, `es-419`, `es-ES`): the translated columns come from
   *  the translation, products without one are left out. GMC only; absent = English. */
  locale?: string;
}

/**
 * Output file format for a triggered export. Omitted on the wire = CSV (server
 * default). `TXT` is byte-identical to CSV (comma-delimited) — only the file
 * name/content-type differ; `TSV` is tab-delimited.
 */
export type ExportFormat = 'CSV' | 'TSV' | 'TXT';

/** Body shared by `POST /estimate` and `POST /trigger`. */
export interface ExportTriggerRequest {
  sourceWorkflowId: string;
  tenantContext: ExportTenantContext;
  filters?: ExportFilters;
  /** Output file format. Omitted → CSV. `/estimate` accepts but ignores it. */
  format?: ExportFormat;
  /**
   * Delivery columns to emit, as connector wire names. `null`, `[]` and omitted
   * are all equivalent on the server and mean **every field for the connector**
   * (50 GMC / 23 META) — which is what we send today.
   *
   * Order is never taken from this array: the server always emits `id`,
   * `retailer_id`, then its own field-map declaration order, so a given
   * selection always yields the same column layout. An unrecognised name is a
   * `400` naming every offender.
   */
  fields?: string[] | null;
  /**
   * Template code from `GET /exports/templates` — decides how each selected
   * column is *encoded*, not which columns are emitted. Omitted/null means the
   * connector's default template, which is the raw one, so a request without
   * this is byte-identical to what we sent before templates existed.
   *
   * An unknown code is a `400`, never a silent fallback to raw: a caller asking
   * for a feed encoding must not receive unencoded columns.
   */
  template?: string | null;
}

/**
 * A file layout in force for a download (§9). Such exports start on
 * agentic-integrations (`POST /v1/transformer-exports`), not export-service's
 * trigger; `transformerId` only on a Lily user's explicit pick (else the
 * subsidiary default for the connector).
 */
export interface ExportLayout {
  transformerId?: string;
  transformerVersion?: number;
}

/** One file a transformed export wrote (`files[]` on the status DTO, from the manifest). */
export interface ExportStatusFile {
  fileName: string;
  path: string;
  subfolder: string | null;
  sizeBytes: number | null;
  /** export-service-signed URL; null until signed. */
  downloadUrl: string | null;
  downloadUrlExpiresAt: string | null;
}

/** `POST /estimate` response — powers the pre-trigger confirmation dialog. */
export interface ExportEstimateResponse {
  expectedProducts: number;
  expectedFileSizeBytes: number;
  expectedChunks: number;
  /**
   * Products this run's curation skipped as already-curated, or null when it
   * could not be determined — the job has no curation run (file uploads,
   * experiments), or curation was unreachable.
   *
   * Null and 0 mean different things and must stay apart: 0 is "nothing was
   * skipped, the run produced nothing for another reason", null is "we cannot
   * say". Guard on `> 0`, never on presence, or an export that was never
   * curated would claim products were skipped.
   */
  alreadyCurated: number | null;
}

/** `POST /trigger` response. `status` is always the literal `"PENDING"` here (§5.1). */
export interface ExportTriggerResponse {
  exportId: string;
  workflowId: string;
  runId: string;
  status: ExportStatus;
}

/**
 * `GET /{exportId}` and every SSE `status` frame share this shape. Progress is
 * computed from `writtenProducts / expectedProducts` (fallback to chunk counts).
 * `downloadUrl` is a freshly-minted 48h signed GCS URL, present only on COMPLETED.
 */
export interface ExportStatusResponse {
  exportId: string;
  sourceWorkflowId: string;
  workflowId: string;
  status: ExportStatus;
  currentStage: string | null;
  expectedProducts: number;
  writtenProducts: number;
  chunksTotal: number;
  chunksWritten: number;
  storagePath: string | null;
  downloadUrl: string | null;
  downloadUrlExpiresAt: string | null;
  failureReason: string | null;
  createdAt: string | null;
  startedAt: string;
  completedAt: string | null;
  liveStage: unknown;
  /** The file transformer and version the export ran (null on the standard path / older rows). */
  transformerId?: string | null;
  transformerName?: string | null;
  transformerVersion?: number | null;
  /** Every file the export wrote (several when a transformer splits). */
  fileNames?: string[] | null;
  /** Subfolder a file step put the files in. */
  subfolder?: string | null;
  storagePaths?: string[] | null;
  /** Every file with its own signed URL (§7b); `[]` on a standard export / before the manifest. */
  files?: ExportStatusFile[] | null;
  /** When the file stops being downloadable (completion + retention, 90 days); null without
   *  a file. Optional — older responses omit it. */
  expiresAt?: string | null;
  /** Past `expiresAt`: still listed, but download / refresh / preview answer 410. */
  expired?: boolean;
  /** Who triggered the export (workflow-service audit stamp, string actor). */
  createdBy?: string | null;
  /**
   * Row/column facts captured at completion (`aes-delivery`'s `docs/UI-API-CONTRACT.md`).
   * `liveness` mirrors the catalog request's own `catalog.liveness` block, frozen at trigger;
   * absent when the export never carried one. `columns` is a FILL-RATE map keyed by output
   * column name (not a name list) — distinct from a export-profile-run's own `facts.columns`
   * (a plain string array), which is agentic-integrations' concept, not this one. NOT on list
   * items — `archivedAt` is the reverse (list-only, absent here); the export page reflects
   * archive state from its own action rather than this read. */
  facts?: {
    rows: number;
    skus: number;
    columns: Record<string, { filled: number; empty: number }>;
    liveness?: { lastSyncedAt: string | null; droppedNotLive: number } | null;
    shippedIdsFile?: string | null;
  } | null;
}

/** `GET /exports` list item — no `downloadUrl` (minted on read via detail/refresh). */
export interface ExportListItem {
  exportId: string;
  sourceWorkflowId: string;
  workflowId: string;
  status: ExportStatus;
  /** Output file format (CSV/TSV/TXT). Optional — older responses omit it. */
  format?: ExportFormat;
  /** Generated download file name. Optional — older responses omit it. */
  fileName?: string | null;
  expectedProducts: number;
  writtenProducts: number;
  createdAt: string | null;
  /** The export's language: `en-US` (English — every export from before translated exports)
   *  or the translated locale. Optional — responses before the field existed omit it. */
  locale?: string;
  /** Who triggered the export (workflow-service audit stamp, string actor).
   *  Optional — older responses omit it; rendered via `ActorStringLabel`. */
  createdBy?: string | null;
  completedAt: string | null;
  /** The file transformer and version the export ran. Optional — older responses omit them. */
  transformerName?: string | null;
  transformerVersion?: number | null;
  /** When the file stops being downloadable (completion + retention, 90 days); null without
   *  a file. Optional — older responses omit it. */
  expiresAt?: string | null;
  /** Past `expiresAt`: still listed, but download / refresh / preview answer 410. */
  expired?: boolean;
  /** `GMC`/`META` for a catalog export, `null` otherwise (`aes-delivery`'s in-progress
   *  `ExportListItemDto` extension — see docs "API assumptions to verify"). */
  connector?: ExportConnector | null;
  firstFileName?: string | null;
  fileCount?: number;
  sizeBytes?: number | null;
  /** When archived (`POST /exports/{id}/archive`); `null` = not archived. */
  archivedAt?: string | null;
}

/** Sort keys `GET /exports` accepts (`aes-delivery`'s `_LIST_SORT_KEYS`). */
export type ExportListSortKey = 'createdAt' | 'rows' | 'status';
export type SortOrder = 'asc' | 'desc';

/** `POST /exports/{id}/archive` and `/restore` — single-id and bulk form alike.
 *  `updated` is the ACTUAL wire key (`ExportArchiveResultDto.archived` has
 *  `serialization_alias="updated"` server-side) — do not rename this field to
 *  `archived`, a prior pass here did and `.json<ExportArchiveResult>().archived`
 *  would silently read `undefined`. */
export interface ExportArchiveResult {
  updated: number;
}

/** `POST /exports/archive` / `/restore` (bulk) body. Server caps this at `MAX_ARCHIVE_IDS`
 *  (documented as 500; not independently confirmed in code — see docs). */
export interface ExportBulkArchiveRequest {
  ids: string[];
}

/** A catalog export's selection, as captured at trigger (`ExportConfigSnapshot.catalog`). */
export interface ExportCatalogConfig {
  connector: string;
  versionRule: string;
  /** The batch selection as requested: `preset`, `includeBatchIds`, dates… */
  batchSelection: Record<string, unknown>;
  /** The batches it resolved to, in priority order. `createdAt` is `null` on a snapshot recorded
   *  before the field existed (`UI-API-CONTRACT.md`'s "new" addition). */
  batches: { batchId: string; name: string | null; createdAt: string | null }[];
  pins: number;
  excludedSkus: number;
  excludedProducts: number;
  packaging: Record<string, unknown>;
  /** The grid narrowing it kept: `search`, `columnFilters`, `externalIds` (a count). */
  narrowed: Record<string, unknown> | null;
  /** The whole catalog spec as sent (batches, versionRule, pins, exclude…): Recent exports read
   *  the batch selection from it. Absent on snapshots recorded before it was kept. */
  spec?: Record<string, unknown> | null;
}

/** `GET /v1/exports/{id}/config` — the configuration an export ran with. */
export interface ExportConfigSnapshot {
  version: number;
  kind: 'BATCH' | 'CATALOG';
  capturedAt: string;
  requestedBy: string | null;
  format: string;
  /** Template code the columns came from; `null` = raw columns. */
  template: string | null;
  transformer: { id: string; name: string; version: number } | null;
  /** Requested columns; `null` = the default set. */
  fields: string[] | null;
  filters: Record<string, unknown>;
  options: Record<string, unknown> | null;
  catalog: ExportCatalogConfig | null;
}

/** `POST /trigger` 409 body — an identical export is already running. */
export interface ExportConflictBody {
  message: string;
  existingWorkflowId: string;
  existingExportId?: string;
  errors?: string[];
}

/** `POST /{exportId}/downloadUrl:refresh` response — a fresh 48h signed URL. */
export interface ExportDownloadUrlResponse {
  downloadUrl: string;
}

/** Optional filters for the `GET /exports` list endpoint. */
export interface ExportListParams {
  sourceWorkflowId?: string;
  status?: ExportStatus;
  /** ISO instant, inclusive — exports created at or after it. */
  createdFrom?: string;
  /** ISO instant, exclusive — exports created before it. */
  createdTo?: string;
  limit?: number;
  /** Defaults server-side to `createdAt`/`desc`. */
  sort?: ExportListSortKey;
  order?: SortOrder;
  /** Opaque keyset cursor from the previous page's `X-Next-Cursor` response header. */
  cursor?: string;
  /** Defaults to `false` server-side. */
  archived?: boolean;
  /** Free-text match over export id + workflow id only — NOT file names (server limitation). */
  q?: string;
}

/**
 * `GET /v1/exports/counts?sourceWorkflowIdPrefix=&archived=&createdFrom=` (`aes-delivery`
 * `docs/catalog-export-api.md` §4.9c). Confirmed contract: `archived=true` is a 503 until the
 * workflow-service archive column exists (same friendly state as the list itself); `archived` in
 * the response is `null` before that column exists (render nothing, not 0); `createdSince` is
 * present ONLY when `createdFrom` was sent on the request. "In progress" tab = PENDING +
 * IN_PROGRESS; "Ready to download" = COMPLETED − expired (expired COMPLETED rows no longer offer
 * a download, so they don't belong in that tab's count).
 */
export interface ExportCountsParams {
  /** Scopes counts to catalog exports only (`"catalog:"`) — this UI's list never wants a count
   *  across every export kind the service serves. */
  sourceWorkflowIdPrefix?: string;
  archived?: boolean;
  /** ISO instant with a `Z`/offset — only when the caller wants `createdSince` back. */
  createdFrom?: string;
}

export interface ExportCountsResponse {
  byStatus: Partial<Record<ExportStatus, number>>;
  expired: number;
  /** `null` before the workflow-service archive column is deployed. */
  archived: number | null;
  /** Present only when the request carried `createdFrom`. */
  createdSince?: number;
  oldestReadyAt: string | null;
}

/** Which ad-platform column set the export produces — drives display copy only. */
export type ExportConnector = 'GMC' | 'META';

/** Terminal states never leave that state; used to stop the SSE / polling. */
export const TERMINAL_EXPORT_STATUSES: readonly ExportStatus[] = [
  'COMPLETED',
  'FAILED',
  'CANCELLED',
];

/** True once the export row has reached a state it can never leave. */
export function isTerminalExportStatus(status: ExportStatus | undefined): boolean {
  return status !== undefined && TERMINAL_EXPORT_STATUSES.includes(status);
}

// ---- export templates config -------------------------------------------
//
// `GET /exports/templates` — static, tenant-independent, version-stamped. Wire
// names mirror the service's serialization aliases verbatim; nothing here is
// derived, so the picker shows exactly what the writer will produce.

/**
 * One selectable column. `columns` is the backing enrichment column(s) — one
 * for most fields, two for META's composite `price` / `salePriceEffectiveDate`.
 */
export interface ExportTemplateColumn {
  key: string;
  label: string;
  /** How the raw cell renders (`SCALAR`, `JSON`, `ARRAY`, …) — display only. */
  kind: string;
  columns: string[];
  identifier: boolean;
  /**
   * Whether this column can be filtered server-side. False for JSON/ARRAY
   * kinds (the cell is a re-serialisation of the stored shape), derived fields
   * and composites — the browse endpoint rejects those, so no input is drawn.
   * Optional: a service predating it omits the key.
   */
  filterable?: boolean;
  /** The vendor's published limits, when its spec defines any. Advisory: the
   *  export neither truncates nor rejects on them. */
  spec?: {
    maxLength?: number;
    maxRepetitions?: number;
    docUrl?: string;
  } | null;
  /**
   * The `settings.options[]` code that must be on for this column to be in the
   * file (META's 106 category fields need `flattenAdditionalFeatures`). Asking
   * for one without the option is a 400 "Unknown META fields", so pickers only
   * offer it while the option is on — see `availableColumns`.
   */
  requiresOption?: string | null;
  /**
   * How a filter on this column matches (catalog grid): `contains` (substring),
   * `element` (any element of a list column), `content` (the text inside a
   * `{content, …}` value); `null` when it cannot be filtered.
   */
  filterMode?: 'contains' | 'element' | 'content' | null;
  /**
   * Whether the catalog grid can filter this column on "has a value / is empty" —
   * every filterable column plus JSON ones with no text to match (`product_detail`).
   */
  presenceFilterable?: boolean;
}

/**
 * One template — a named set of per-column encodings, NOT a column subset.
 * `transformations` is a delta: any column it doesn't name is `PASSTHROUGH`, so
 * a raw template is `{}` rather than a full table.
 */
export interface ExportTemplateDefinition {
  code: string;
  label: string;
  description: string;
  /** Exactly one template per connector is the default, and it is the raw one. */
  default: boolean;
  /** `{ columnKey: transformationCode }`. */
  transformations: Record<string, string>;
  /**
   * Record pre-passes this template applies, by name.
   *
   * Not inferable from `transformations`: an expansion rewrites the record
   * before any cell renders, so it can change which columns the file contains.
   * `DESCRIPTOR_INTO_DESCRIPTION` folds the selected descriptor into
   * `description` and drops its own column — which is why the picker offers
   * the two descriptors as an either/or and hides the pre-joined pair.
   *
   * Optional: a service predating the field omits the key, and the picker then
   * simply offers every column with no exclusivity — the server still rejects
   * an impossible pair, so the degraded path is safe.
   */
  expansions?: string[];
  /**
   * The identifier columns this template always writes, in file order —
   * whatever `fields` says. `gmc-raw` writes `id` + `retailer_id`;
   * `gmc-merchant-center-upload` writes only `id` (the Merchant Center id).
   *
   * Shown as always included rather than as switches: the server ignores an
   * identifier in `fields`, so a switch for one does nothing. Per template,
   * because `columns[].identifier` is the same for every template of a
   * connector. Optional: a service predating the field omits the key, and the
   * picker keeps offering the connector's identifier columns as switches.
   */
  identifiers?: string[];
  /**
   * The source columns each of `identifiers` is read from. `id` is Lily's
   * internal `product_id` on `gmc-feed` but the Merchant Center id on
   * `gmc-merchant-center-upload` — the connector column says `product_id`
   * for both, so "is this the internal id?" is judged here. Optional: a
   * service predating it omits the key.
   */
  identifierColumns?: Record<string, string[]>;
  /**
   * Connector columns this template never writes. The server drops them from a request
   * (with a logged warning) rather than failing it; the picker hides them. Optional: a
   * service predating the field omits the key, and every column stays offered.
   */
  excludeFields?: string[];
}

/**
 * One named, one-click subset of a connector's columns.
 *
 * `fields` resolves to the existing `fields` request parameter — a preset is a
 * shortcut to a selection, not a new request shape. Meta's supplemental-feed
 * preset is the motivating case: a supplemental upload writes whatever it
 * carries, blanks included, over the matching field on every catalog product,
 * so columns that are empty on this export have to be omitted rather than sent.
 */
export interface ExportFieldPreset {
  code: string;
  label: string;
  description: string;
  fields: string[];
}

/**
 * One connector's columns and templates. `enrichmentConnectorCode` (`GMC` /
 * `META`) is what `enrichment_job.connector_code` stores and matches our
 * `ExportConnector`; `connectorCode` is the service's own display key
 * (`GOOGLE_MERCHANT_CENTER` / `META_COMMERCE`) and is not interchangeable.
 */
export interface ExportTemplateConnector {
  connectorCode: string;
  enrichmentConnectorCode: ExportConnector;
  label: string;
  table: string;
  /** The vendor feed spec this connector's encodings reproduce. */
  specUrl: string;
  columns: ExportTemplateColumn[];
  templates: ExportTemplateDefinition[];
  /** Named field-selection shortcuts for the column picker. Empty for GMC, and
   *  optional because a service predating presets omits the key entirely. */
  fieldPresets?: ExportFieldPreset[];
}

/** One encoding a template can apply, as a real before/after pair. */
export interface ExportTransformation {
  code: string;
  label: string;
  description: string;
  before: string;
  after: string;
}

/** One output format offered in the export settings. */
export interface ExportFormatOption {
  code: ExportFormat;
  label: string;
  extension: string;
  delimiter: string;
  contentType: string;
  default: boolean;
  /** Why you might pick this one — mainly the comma collision between a CSV
   *  delimiter and the separator the feed encodings use internally. */
  note: string;
  /** Advisory: Google recommends TSV for text feeds. Both formats work. */
  recommendedForFeedTemplates: boolean;
}

/** One run-shape switch (`settings.options[]`), sent back inside the request's `options`. */
export interface ExportRunOption {
  code: string;
  label: string;
  description: string;
  default: boolean;
  /** Connector display codes it does anything for (`META_COMMERCE`). */
  appliesTo: string[];
  note: string;
}

/** The request's `options` object — run-shape switches, all off by default. */
export interface ExportOptions {
  flattenAdditionalFeatures?: boolean;
  skipEmptyRows?: boolean;
}

/** `GET /exports/templates` — the whole static config. */
export interface ExportTemplateConfig {
  version: number;
  settings: { formats: ExportFormatOption[]; options?: ExportRunOption[] };
  transformations: ExportTransformation[];
  connectors: ExportTemplateConnector[];
}

/** Body of `POST /exports/templates/preview` — the trigger body plus `rows`. */
export interface ExportTemplatePreviewRequest extends ExportTriggerRequest {
  /** Products to render. Server bounds this to 1–50; omitted → 5. */
  rows?: number;
}

/** One sampled product rendered both ways. Same length, same order as `header`. */
export interface ExportTemplatePreviewRow {
  raw: string[];
  encoded: string[];
}

/**
 * `POST /exports/templates/preview`. `rowCount` can be below the requested
 * `rows` (the run holds fewer products, or `fieldFilters` dropped some after
 * rendering); `truncated` says more products exist beyond what was read.
 *
 * The endpoint accepts `format` for request-shape parity but ignores it — it
 * returns cells, not a delimited line, so no delimiter applies.
 */
export interface ExportTemplatePreview {
  /** Wire fields the run was configured to enrich; see `ExportTemplateBrowse`.
   *  Optional because a service predating it omits the key entirely. */
  runFields?: string[];
  header: string[];
  rows: ExportTemplatePreviewRow[];
  rowCount: number;
  truncated: boolean;
}

/**
 * Body of `POST /exports/templates/browse` — the trigger body plus paging.
 *
 * The preview's sibling rather than a bigger preview: the preview caps at 50
 * rows because it renders every row twice and returns them inline, which is
 * right for a column picker and wrong for scanning a 34k-product run.
 */
export interface ExportTemplateBrowseRequest extends ExportTriggerRequest {
  /** Rows per page. Server bounds this to 1–200. */
  pageSize?: number;
  /**
   * The previous page's `nextCursor`, passed back verbatim. Omit for page one.
   * Exclusive, and keyset rather than an offset — which is why the table shows
   * Prev/Next instead of numbered pages: an arbitrary page has no cursor.
   */
  cursor?: string | null;
  /** Free-text match over the run's identifiers and title, server-side. */
  search?: string | null;
  /**
   * Per-column substring filters, `{ wireField: needle }`, applied in SQL so
   * they narrow the whole run rather than the page on screen. Only fields
   * backed by a single scalar column may appear — anything else is a 400, which
   * is why the table only draws inputs for `filterable` columns.
   */
  columnFilters?: Record<string, string> | null;
}

/** One page of `POST /exports/templates/browse`. */
export interface ExportTemplateBrowse {
  header: string[];
  rows: ExportTemplatePreviewRow[];
  rowCount: number;
  /** Rows matching the filters and search across the WHOLE run, not this page. */
  total: number;
  /** Null on the last page. Derived server-side by reading one row beyond the
   *  page, so a page that exactly empties the run does not advertise another. */
  nextCursor: string | null;
  /** The run's connector, echoed so a caller holding only a run id (this view is
   *  reached by URL) can resolve columns and templates without a second lookup. */
  connectorCode: ExportConnector;
  /**
   * Wire fields the RUN was configured to enrich. Empty means "not recorded",
   * never "the run selected nothing" — callers fall back to every column there.
   */
  runFields: string[];
}

/** Applied to any column a template does not name — the identity encoding. */
export const PASSTHROUGH_TRANSFORMATION = 'PASSTHROUGH';

/**
 * Whether `POST /trigger` can honour a `template`.
 *
 * `true` since `endpoints.exports.*` moved onto `exportClient`. Kept as a
 * function rather than deleted so the capability stays named at its call sites,
 * and so a backend regression is one edit to re-gate.
 *
 * It was `false` while the trigger was served by **workflow-webapp**
 * (`/workflow/v1/exports/trigger`) and templates by **agentic-export-service**.
 * Spring ignores unknown JSON fields, so a `template` on the Java trigger was
 * dropped silently and the export came back RAW — a file that looks valid and is
 * not encoded, the one outcome the template contract exists to prevent.
 *
 * Three backend fixes had to land before this could flip. It was re-applied and
 * reverted twice because only some of them had:
 *   1. iac-modules #684 — `export_user` held SELECT-only on the `workflow`
 *      schema, so the trigger's INSERT was denied and answered a bare 500 for
 *      RAW exports too, while every read returned 200.
 *   2. iac-modules #685 — `TEMPORAL_TARGET` was unset, so the trigger dialled
 *      `localhost:7233`.
 *   3. agentic-export-service #40 — the export runs in-process in the API
 *      (`export_execution_mode = "inprocess"`), so it executes without a
 *      Temporal worker deployment, which had never existed in any environment.
 */
export function supportsTemplateTrigger(): boolean {
  return true;
}
